import { useMemo } from 'react';
import _ from 'lodash';
import { FormFeedback, FormGroup, Label } from 'reactstrap';
import { Field, useFormikContext } from 'formik';
import { gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useDataContext } from '../../contexts/DataContext';
import GroupedTypeahead from '../GroupedTypeahead';
import { useQueryWithFarmId } from '../../utils/hooks';
import { optionsAreEqualById } from '../basics/StandardTypeahead';

const TemplateFuelOptionsQuery = gql`
  query TemplateFuelOptions {
    fuelResources {
      id
      name
    }
  }
`;

const TeamFuelOptionsQuery = gql`
  query TeamFuelOptions($id: ID!) {
    team(id: $id) {
      id
      pricingResources {
        id
        type
        name
      }
    }
  }
`;

const FarmFuelOptionsQuery = gql`
  query FarmFuelOptions($farmId: ID!) {
    fuelResources {
      id
      name
    }
    farmTeamPricingTemplates(farmId: $farmId) {
      id
      type
      name
    }
    rawFuels(farmId: $farmId) {
      id
      resourceId
      name
    }
  }
`;

const filterPricingResource = (pricing) => pricing?.type === 'fuel';

const filterFuelResources = (fuelResources, resourceIdMap) =>
  fuelResources.filter((input) => !resourceIdMap[input.id]);

const FuelSelector = () => {
  const { isTemplate } = useDataContext();
  const { teamId } = useParams();
  const { data } = useQueryWithFarmId(
    isTemplate
      ? teamId
        ? TeamFuelOptionsQuery
        : TemplateFuelOptionsQuery
      : FarmFuelOptionsQuery,
    {
      includeFarmId: !isTemplate,
      variables: { id: teamId },
      // Get latest data if team or public fuel resource instantiated on a farm
      fetchPolicy: 'cache-and-network',
    },
  );
  const { values, setFieldValue } = useFormikContext();

  const resourceIdMap = useMemo(
    () => _.keyBy(data?.rawFuels || [], 'resourceId'),
    [data],
  );

  const fuelGroups = useMemo(
    () => [
      { header: 'Farm Fuels', options: data?.rawFuels || [] },
      {
        header: 'Team Fuels',
        options: filterFuelResources(
          (data?.team?.pricingResources || []).filter(filterPricingResource),
          resourceIdMap,
        ),
      },
      {
        header: 'Team Fuels',
        options: filterFuelResources(
          (data?.farmTeamPricingTemplates || []).filter(filterPricingResource),
          resourceIdMap,
        ),
      },
      {
        header: 'Public Fuels',
        options: filterFuelResources(data?.fuelResources || [], resourceIdMap),
      },
    ],
    [data, resourceIdMap],
  );

  return (
    <Field name="pricing">
      {({ meta: { touched, error } }) => {
        return (
          <FormGroup>
            <Label>Fuel Type</Label>
            <div>
              <GroupedTypeahead
                id="fuel-selector"
                align="left"
                clearTextOnFocus
                isInvalid={!!(touched && error)}
                labelKey="name"
                maxWidth="250px"
                onChange={([v]) => setFieldValue('pricing', v)}
                optionGroups={fuelGroups}
                optionsAreEqual={optionsAreEqualById}
                placeholder={values?.pricing?.name || 'Fuel Type'}
                positionFixed
                selectedOption={values?.pricing}
                size="sm"
              />
              {touched && error && (
                <FormFeedback className="d-block">{error}</FormFeedback>
              )}
            </div>
          </FormGroup>
        );
      }}
    </Field>
  );
};

export default FuelSelector;
